// ChatParticipantsModal.jsx
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

import {
    addParticipantToRoom,
    removeParticipantFromRoom,
} from "../../actions/chatActions";

const getId = (value) => String(value?._id || value || "");

const getUserName = (user) => {
    if (!user) return "Пользователь";

    const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();

    return name || user.email || user.username || "Пользователь";
};

const collectOfficeUsers = (state, officeId) => {
    const byOffice = state.officeMember?.membersByOffice || state.officeMember?.currentMembersByOffice || {};
    const fromOffice = byOffice?.[officeId];

    if (Array.isArray(fromOffice)) return fromOffice.map((item) => item.userId || item.user || item).filter(Boolean);

    const flat = state.officeMember?.members || state.officeMember?.officeMembers || state.officeMember?.items || [];

    if (Array.isArray(flat)) {
        return flat.map((item) => item.userId || item.user || item).filter(Boolean);
    }

    const office = (state.office?.offices || []).find((item) => String(item._id || item.id) === String(officeId));

    if (office?.allowedUsers) return office.allowedUsers;

    return [];
};

export default function ChatParticipantsModal({ chat, officeId, open, onClose, onUpdated }) {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.auth || {}, shallowEqual);
    const officeUsers = useSelector((state) => collectOfficeUsers(state, officeId), shallowEqual);

    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const participants = useMemo(() => {
        return Array.isArray(chat?.participants) ? chat.participants : [];
    }, [chat]);

    const participantIds = useMemo(() => {
        return new Set(participants.map(getId));
    }, [participants]);

    const candidates = useMemo(() => {
        const q = search.trim().toLowerCase();

        return (officeUsers || [])
            .filter((item) => item && !participantIds.has(getId(item)))
            .filter((item) => {
                if (!q) return true;

                return `${item.firstName || ""} ${item.lastName || ""} ${item.email || ""} ${item.username || ""}`.toLowerCase().includes(q);
            });
    }, [officeUsers, participantIds, search]);

    if (!open) return null;

    const canManage = chat?.isGroup && chat?.chatType !== "saved";

    const handleAdd = async (userId) => {
        if (!chat?._id || !userId || busy) return;

        setBusy(true);
        setError("");

        try {
            const result = await dispatch(addParticipantToRoom(chat._id, userId));
            if (result?.chat) onUpdated?.(result.chat);
        } catch (requestError) {
            setError(requestError?.response?.data?.error || requestError?.message || "Не удалось добавить участника");
        } finally {
            setBusy(false);
        }
    };

    const handleRemove = async (userId) => {
        if (!chat?._id || !userId || busy) return;
        if (getId(userId) === getId(user?._id)) return;

        setBusy(true);
        setError("");

        try {
            const result = await dispatch(removeParticipantFromRoom(chat._id, userId));
            if (result?.chat) onUpdated?.(result.chat);
        } catch (requestError) {
            setError(requestError?.response?.data?.error || requestError?.message || "Не удалось изменить участников");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="chat-modal-AO" onMouseDown={onClose}>
            <div className="chat-modal-AO__body chat-modal-AO__body--participants" onMouseDown={(event) => event.stopPropagation()}>
                <div className="chat-modal-AO__head">
                    <div className="chat-modal-AO__title">Участники чата</div>
                    <button type="button" onClick={onClose}>✕</button>
                </div>

                <div className="chat-participants-AO">
                    {error && <div className="chat-participants-AO__error" role="alert">{error}</div>}
                    <section className="chat-participants-AO__section">
                        <div className="chat-participants-AO__section-title">Сейчас в чате</div>

                        <div className="chat-participants-AO__list">
                            {participants.map((participant) => (
                                <div key={getId(participant)} className="chat-participants-AO__row">
                                    <div className="chat-participants-AO__avatar">{getUserName(participant).slice(0, 1).toUpperCase()}</div>
                                    <div className="chat-participants-AO__main">
                                        <div className="chat-participants-AO__name">{getUserName(participant)}</div>
                                        <div className="chat-participants-AO__email">{participant.email || ""}</div>
                                    </div>

                                    {canManage && getId(participant) !== getId(user?._id) && (
                                        <button type="button" disabled={busy} onClick={() => handleRemove(getId(participant))}>
                                            Удалить
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>

                    {canManage ? (
                        <section className="chat-participants-AO__section">
                            <div className="chat-participants-AO__section-title">Добавить сотрудника</div>

                            <input
                                className="chat-participants-AO__search"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Поиск по имени или email"
                            />

                            <div className="chat-participants-AO__list">
                                {candidates.map((candidate) => (
                                    <div key={getId(candidate)} className="chat-participants-AO__row">
                                        <div className="chat-participants-AO__avatar">{getUserName(candidate).slice(0, 1).toUpperCase()}</div>
                                        <div className="chat-participants-AO__main">
                                            <div className="chat-participants-AO__name">{getUserName(candidate)}</div>
                                            <div className="chat-participants-AO__email">{candidate.email || ""}</div>
                                        </div>

                                        <button type="button" disabled={busy} onClick={() => handleAdd(getId(candidate))}>
                                            Добавить
                                        </button>
                                    </div>
                                ))}

                                {!candidates.length && <div className="chat-participants-AO__empty">Нет доступных пользователей</div>}
                            </div>
                        </section>
                    ) : (
                        <div className="chat-participants-AO__notice">Управление участниками доступно только для групповых чатов.</div>
                    )}
                </div>
            </div>
        </div>
    );
}
